import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  X, ChevronLeft, ChevronRight, Play, ExternalLink, Image as ImageIcon,
  Video, Loader2, Lock, Globe, Church
} from "lucide-react";
import { getUserMedia, resolveImageUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";

/**
 * Media Gallery component for user profiles.
 * Displays photos and videos in a grid with lightbox viewing.
 */
export default function MediaGallery({ userId, isOwnProfile }) {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadMedia();
  }, [userId]);

  const loadMedia = async (offset = 0) => {
    try {
      setLoading(true);
      const response = await getUserMedia(userId, 50, offset);
      if (offset === 0) {
        setMedia(response.data.media);
      } else {
        setMedia(prev => [...prev, ...response.data.media]);
      }
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
    } catch (err) {
      console.error("Failed to load media:", err);
      setError("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    loadMedia(media.length);
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  const goToPost = (postId) => {
    closeLightbox();
    // Navigate to dashboard with the post highlighted (or a post detail page if you have one)
    navigate(`/dashboard?post=${postId}`);
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case "parish":
        return <Church className="w-3 h-3" />;
      case "private":
        return <Lock className="w-3 h-3" />;
      default:
        return <Globe className="w-3 h-3" />;
    }
  };

  // Empty state
  if (!loading && media.length === 0) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
        <p className="text-[#6B665E] font-['Nunito'] text-sm">
          {isOwnProfile ? "You haven't shared any photos or videos yet" : "No photos or videos to show"}
        </p>
      </div>
    );
  }

  const currentMedia = media[currentIndex];

  return (
    <div>
      {/* Grid Gallery - 4 items per row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {media.map((item, index) => (
          <button
            key={item.id}
            onClick={() => openLightbox(index)}
            className="relative aspect-square bg-[#F0EBE0] rounded-md overflow-hidden group hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] focus:ring-offset-2"
            data-testid={`media-item-${index}`}
          >
            {/* Thumbnail */}
            {item.type === "image" ? (
              <img
                src={resolveImageUrl(item.url)}
                alt={item.caption || "Photo"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : item.type === "cloudflare_video" ? (
              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <Play className="w-10 h-10 text-white/80" />
              </div>
            ) : (
              <div className="w-full h-full relative">
                <video
                  src={resolveImageUrl(item.url)}
                  className="w-full h-full object-cover"
                  preload="metadata"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <Play className="w-10 h-10 text-white/90" />
                </div>
              </div>
            )}
            
            {/* Type indicator */}
            <div className="absolute top-2 right-2 bg-black/50 rounded-full p-1">
              {item.type === "image" ? (
                <ImageIcon className="w-3 h-3 text-white" />
              ) : (
                <Video className="w-3 h-3 text-white" />
              )}
            </div>
            
            {/* Privacy indicator */}
            {item.scope !== "public" && (
              <div className="absolute top-2 left-2 bg-black/50 rounded-full p-1 text-white">
                {getScopeIcon(item.scope)}
              </div>
            )}
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          </button>
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loading}
            className="font-['Nunito']"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              `Load More (${media.length} of ${total})`
            )}
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && media.length === 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-square bg-[#F0EBE0] rounded-md animate-pulse" />
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/20 rounded-full"
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Navigation arrows */}
            {media.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 rounded-full"
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 rounded-full"
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}

            {/* Media content */}
            <div className="flex items-center justify-center min-h-[60vh] max-h-[80vh] p-4">
              {currentMedia?.type === "image" ? (
                <img
                  src={resolveImageUrl(currentMedia.url)}
                  alt={currentMedia.caption || "Photo"}
                  className="max-w-full max-h-[75vh] object-contain rounded-lg"
                />
              ) : currentMedia?.type === "cloudflare_video" ? (
                <div className="w-full max-w-3xl">
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      src={`https://iframe.cloudflarestream.com/${currentMedia.cloudflare_video_id}`}
                      className="absolute top-0 left-0 w-full h-full border-0 rounded-lg"
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      title="Video"
                    />
                  </div>
                </div>
              ) : currentMedia?.type === "video" ? (
                <video
                  src={resolveImageUrl(currentMedia.url)}
                  className="max-w-full max-h-[75vh] rounded-lg"
                  controls
                  autoPlay
                />
              ) : null}
            </div>

            {/* Caption and actions */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-12">
              <div className="flex items-end justify-between">
                <div className="flex-1 mr-4">
                  {currentMedia?.caption && (
                    <p className="text-white/90 text-sm font-['Nunito'] line-clamp-2">
                      {currentMedia.caption}
                    </p>
                  )}
                  <p className="text-white/60 text-xs mt-1 font-['Nunito']">
                    {currentIndex + 1} of {media.length}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => goToPost(currentMedia?.post_id)}
                  className="font-['Nunito'] flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Post
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
